import * as React from 'react'
import { ScrollRow } from './ScrollRow'
import { Chevron } from './Chevron'
import { Focusable } from './Focusable'
import { MovieTileSkeleton } from './MovieTileSkeleton'
import type { LinkComponent } from '../types'

/** How many placeholder tiles the loading row shows — enough to fill the widest
 *  viewport. The count only affects horizontal fill; the reserved row height is
 *  the same for one skeleton or many. */
const SKELETON_COUNT = 6

export interface ShowcaseProps {
  /** Row heading. */
  title: string;
  /**
   * Optional label before the title, naming what kind of row this is (e.g.
   * "Discover" ahead of a topic's name). Set apart from the title — lighter and
   * muted — so the two never read as one phrase; keep it out of `title` for the
   * same reason.
   */
  titlePrefix?: string;
  /** Whether the data request has resolved — drives the fade-in / hidden state. */
  loaded: boolean;
  /** Whether there is anything to show; when false, an empty message renders. */
  hasResults: boolean;
  /** The rendered tiles (one per item), scrolled horizontally by the row. */
  children: React.ReactNode;
  /** Show a decorative chevron after the title (browse uses it; news doesn't). */
  chevron?: boolean;
  /**
   * Optional lead paragraph under the heading, above the tiles. Editorial rows
   * (the browse highlight) carry the topic's plain-text teaser here; ordinary
   * catalogue rows leave it out and show heading + tiles only.
   */
  description?: string;
  /**
   * Where the row leads (a topic's detail page). Turns the chevron after the title
   * from decoration into the link itself — the arrow is already the "there is more
   * this way" affordance, so it becomes the target rather than the title text.
   * Requires `chevron`, which is what draws it.
   */
  titleHref?: string;
  /**
   * In-app link component (e.g. React Router's `Link`) — required for
   * {@link ShowcaseProps.titleHref} to render as a link. Without it the chevron
   * stays decorative, since the styleguide holds no router of its own.
   */
  LinkComponent?: LinkComponent;
  /**
   * Data request still in flight. While true the row renders placeholder tiles
   * instead of the empty message — so it reserves its final height up front and
   * never jumps (nor flashes "nothing was found") when the real tiles arrive.
   */
  loading?: boolean;
  /**
   * Heading level for the row title. A page renders many rows under one page-level
   * `<h1>`, so rows default to `<h2>`; pass 3 to nest a row under another heading.
   * Keeps the document outline valid rather than emitting a wall of `<h1>`s.
   */
  headingLevel?: 2 | 3 | 4;
}

/**
 * The presentational shell of a titled, horizontally-scrolling row of tiles,
 * shared by the browse and news pilets. Data fetching stays in each pilet (the
 * APIs differ); this owns only the layout: the fade-in container, the heading and
 * the {@link ScrollRow} with skeleton-loading and empty-state fallbacks.
 */
export function Showcase ({ title, titlePrefix, loaded, hasResults, children, chevron, description, titleHref, LinkComponent, loading = false, headingLevel = 2 }: ShowcaseProps) {
  const Heading = `h${headingLevel}` as 'h2' | 'h3' | 'h4'
  const linked = chevron && titleHref && LinkComponent
  // The prefix is split out visually, but it is still part of what the row is
  // called — so anything reading the heading as one string (the link's accessible
  // name) gets both halves.
  const fullTitle = titlePrefix ? `${titlePrefix} ${title}` : title
  return (
    <div className='mlsg-title-list' data-loaded={loaded}>
      <div className='mlsg-title-list__heading'>
        <Heading>
          {titlePrefix ? <span className='mlsg-title-list__prefix'>{titlePrefix}</span> : null}
          {/* The title is its own element so it can be the one thing that gives way
              when the heading runs out of room — a bare text node is an anonymous
              flex item and can't be told to truncate. */}
          <span className='mlsg-title-list__text' title={title}>{title}</span>
          {linked
            ? (
              // A stop of its own, so a remote reaches the row's destination on the
              // way past instead of only the tiles inside it. The arrow is the whole
              // link, so it carries the row title as its accessible name — "→" alone
              // tells a screen reader nothing about where it goes.
              <Focusable className='mlsg-focusable--inline mlsg-title-list__chevron-stop'>
                <LinkComponent to={titleHref} className='mlsg-title-list__link' aria-label={fullTitle}>
                  <Chevron orientation='right' />
                </LinkComponent>
              </Focusable>
              )
            : chevron ? <Chevron orientation='right' /> : null}
        </Heading>
        {description ? <p className='mlsg-title-list__description'>{description}</p> : null}
        <ScrollRow label={fullTitle} locked={loading}>
          {loading
            ? Array.from({ length: SKELETON_COUNT }, (_, i) => <MovieTileSkeleton key={i} />)
            : hasResults
              ? children
              : <p className='mlsg-title-list__empty'>Nothing was found</p>}
        </ScrollRow>
      </div>
    </div>
  )
}
